import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    useTheme,
    useMediaQuery,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    TextField,
    CircularProgress,
    Autocomplete,
} from '@mui/material';
import {
    IconCheck,
    IconChevronDown,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { colors, fonts } from '../../ColorFont/color';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import Congratulations from './Congratulations';
import { url } from '../../ConstUrl/Url';

// ──┬ Constants ─────────────────────────────────────────────
const PRIMARY = colors.primary;
const PRIMARY_DARK = colors.primary_dark || "#149343";

// ──┬ Stepper Component ─────────────────────────────────────────────
const stepperData = (t) => [
    { label: t("cropListing.steps.cropCategory"), state: "done", step: 1 },
    { label: t("cropListing.steps.varietyGrade"), state: "done", step: 2 },
    { label: t('cropListing.steps.pricing'), state: 'done', step: 3 },
    { label: t('cropListing.steps.photos'), state: 'done', step: 4 },
    { label: t('cropListing.steps.locationDelivery'), state: 'active', step: 5 },
];

const Stepper = ({ steps }) => {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
            mb: { xs: 3, md: 5 },
            position: 'relative',
        }}>
            {/* Background connector line */}
            <Box sx={{
                position: 'absolute',
                top: 18,
                left: '10%',
                right: '10%',
                height: '2px',
                backgroundColor: '#E0E5EB',
                zIndex: 0,
            }} />

            {steps.map((step, idx) => {
                const isDone = step.state === 'done';
                const isActive = step.state === 'active';

                return (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {/* Circle */}
                        <Box sx={{
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDone ? PRIMARY : isActive ? PRIMARY : '#E0E5EB',
                            border: isActive ? `3px solid ${PRIMARY}` : 'none',
                            transition: 'all 0.3s ease',
                        }}>
                            {isDone ? (
                                <IconCheck size={18} color="#FFFFFF" stroke={3} />
                            ) : (
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: { xs: '13px', md: '14px' },
                                    color: isActive ? '#FFFFFF' : '#919EAB',
                                }}>
                                    {step.step}
                                </Typography>
                            )}
                        </Box>

                        {/* Label */}
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: isActive ? 700 : isDone ? 600 : 500,
                            fontSize: { xs: '11px', md: '13px' },
                            color: isActive ? '#1C252E' : isDone ? '#1C252E' : '#919EAB',
                            mt: 1,
                            textAlign: 'center',
                            maxWidth: { xs: '72px', sm: '100px' },
                            lineHeight: 1.3,
                        }}>
                            {step.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const LocationDelivery = (props) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // ─── Form State ────────────────────────────────────────────────────────────
    const location = useLocation();
    const cropData = location.state?.cropData || {};
    
    const [notes, setNotes] = useState(cropData.additionalNotes || "");
    const [options, setOptions] = useState(cropData.deliveryOptions || {
        selfPickup: true,
        localDelivery: false,
        transportAvailable: false,
    });
    const [state, setState] = useState(cropData.state || "");
    const [district, setDistrict] = useState(cropData.taluka || ""); // Using taluka as district as per research
    const [showCongrats, setShowCongrats] = useState(false);
    const [errors, setErrors] = useState({});

    // Refs for scrolling to errors
    const stateRef = React.useRef(null);
    const districtRef = React.useRef(null);
    const optionsRef = React.useRef(null);
    const notesRef = React.useRef(null);

    const scrollToFirstError = (newErrors) => {
        if (newErrors.state) {
            stateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            stateRef.current?.focus();
        } else if (newErrors.district) {
            districtRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            districtRef.current?.focus();
        } else if (newErrors.options) {
            optionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (newErrors.notes) {
            notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            notesRef.current?.focus();
        }
    };

    const isEdit = location.state?.isEdit;
    const cropId = location.state?.cropId;

    // --- API State ---
    const [isPublishing, setIsPublishing] = useState(false);
    const [allStates, setAllStates] = useState([]);
    const [allDistricts, setAllDistricts] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const steps = stepperData(t);

    // Validation messages moved to JSON, but using local for now if needed or t()
    // t('cropListing.fieldReq') etc.

    // --- Fetch States ---
    useEffect(() => {
        const fetchStates = async () => {
            setLoadingStates(true);
            try {
                const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ country: 'India' })
                });
                const data = await response.json();
                if (!data.error) {
                    setAllStates(data.data.states || []);
                }
            } catch (err) {
                console.error("Failed to fetch states:", err);
            } finally {
                setLoadingStates(false);
            }
        };
        fetchStates();
    }, []);

    // --- Fetch Cities (Districts) when State changes ---
    useEffect(() => {
        if (!state) {
            setAllDistricts([]);
            setDistrict('');
            return;
        }

        const fetchDistricts = async () => {
            setLoadingDistricts(true);
            setDistrict('');
            try {
                const response = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ country: 'India', state: state })
                });
                const data = await response.json();
                if (!data.error) {
                    setAllDistricts(data.data || []);
                }
            } catch (err) {
                console.error("Failed to fetch cities:", err);
            } finally {
                setLoadingDistricts(false);
            }
        };
        fetchDistricts();
    }, [state]);

    const handlePublish = async () => {
        const userData = props.userData || JSON.parse(localStorage.getItem('userData'));
        const userId = userData?._id || localStorage.getItem('userId');

        if (!userId) {
            toast.error("User session not found. Please login again.");
            return;
        }

        const newErrors = {};
        if (!state) newErrors.state = true;
        if (!district) newErrors.district = true;
        if (!notes || notes.trim() === "") newErrors.notes = true;
        if (!Object.values(options).some(v => v === true)) newErrors.options = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            scrollToFirstError(newErrors);
            return;
        }

        if (!cropData.photos || cropData.photos.length === 0) {
            toast.warning("Please upload at least one photo of your crop");
            return;
        }

        const formData = new FormData();
        
        // Basic Plot fields expected by backend
        formData.append('country', 'India');
        formData.append('state', state);
        formData.append('taluka', district); // Using district for taluka
        formData.append('village', district); 
        formData.append('category', cropData.category || 'Other');
        formData.append('subCategory', cropData.subCategory || '');
        formData.append('grade', cropData.grade || 'A');
        formData.append('variety', cropData.variety || '');
        
        // Backend now handles area as float and calculates areaInSquareMeters
        const areaValue = parseFloat(cropData.area || 0);
        formData.append('area', areaValue);
        
        formData.append('areaUnit', cropData.areaUnit === 'Custom' ? cropData.customAreaUnit : (cropData.areaUnit || 'Acre'));
        formData.append('amount', cropData.amount || '0');
        formData.append('priceUnit', cropData.priceUnit || (cropData.areaUnit === 'Custom' ? cropData.customAreaUnit : cropData.areaUnit) || 'Kg'); 
        formData.append('user', userId);
        
        // Contact number from user data
        const contactNum = userData?.phoneNumber || userData?.phone || "";
        formData.append('contactNumber', contactNum);
        
        formData.append('isAvailable', true);
        formData.append('isNegotiable', cropData.isNegotiable || false);
        formData.append('additionalNotes', notes);
        
        // Delivery Options (JSON stringified for backend parsing)
        formData.append('deliveryOptions', JSON.stringify(options));

        // Add Photos
        if (cropData.photos && cropData.photos.length > 0) {
            cropData.photos.forEach((file) => {
                // Ensure we only append File objects, not strings (if any)
                if (file instanceof File) {
                    formData.append('photos', file);
                } else if (typeof file === 'string') {
                    // This case might happen during Edit if photos aren't changed
                    formData.append('photos', file);
                }
            });
        }

        // --- DEBUG PAYLOAD ---
        console.log("--- PUBLISHING CROP ---");
        for (let pair of formData.entries()) {
            console.log(pair[0] + ': ' + (pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]));
        }

        const apiUrl = isEdit ? `${url}/api/plots/update/${cropId}` : `${url}/api/plots`;
        const apiMethod = isEdit ? 'PUT' : 'POST';

        setIsPublishing(true);
        try {
            const response = await fetch(apiUrl, {
                method: apiMethod,
                body: formData,
            });

            // Try to get JSON response
            let data;
            const text = await response.text();
            try {
                data = JSON.parse(text);
            } catch (e) {
                data = { error: text };
            }

            if (response.ok) {
                console.log("Publish Success:", data);
                toast.success(isEdit ? "Crop updated successfully!" : "Crop listed successfully!");
                setShowCongrats(true);
            } else {
                console.error("Backend Error:", data);
                const errorMsg = data.error || data.message || "Failed to save plot details";
                toast.error(`Error: ${errorMsg}`);
            }
        } catch (error) {
            console.error("Network/API Error:", error);
            toast.error("Network error. Please check your connection.");
        } finally {
            setIsPublishing(false);
        }
    };
    const handleBack = () => {
        const updatedCropData = {
            ...(location.state?.cropData || {}),
            additionalNotes: notes,
            deliveryOptions: options,
            state: state,
            taluka: district,
        };
        navigate('/photos', { 
            state: { 
                cropData: updatedCropData,
                isEdit: location.state?.isEdit,
                cropId: location.state?.cropId
            } 
        });
    };

    const handleOptionChange = (name) => (event) => {
        const newOptions = { ...options, [name]: event.target.checked };
        setOptions(newOptions);
        if (Object.values(newOptions).some(v => v === true)) {
            setErrors(prev => ({ ...prev, options: false }));
        }
    };

    // ─── Select Styles ────────────────────────────────────────────────────────
    const selectSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '15px',
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E0E5EB' },
            '&:hover fieldset': { borderColor: '#919EAB' },
            '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
            '&.Mui-error fieldset': { borderColor: '#FF4842' },
        },
        '& .MuiInputLabel-root': {
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '14px',
            color: '#637381',
            '&.Mui-focused': { color: PRIMARY },
            '&.Mui-error': { color: '#FF4842' },
        },
        '& .MuiFormHelperText-root': {
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '12px',
            fontWeight: 500,
            ml: 1,
        }
    };

    return (
        <Box sx={{
            minHeight: '100%',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
                {/* ── Page Title + Breadcrumb ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pt: { xs: 3, md: 4 },
                    pb: { xs: 1, md: 2 },
                }}>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 800,
                        fontSize: { xs: '22px', md: '28px' },
                        color: '#1C252E',
                        mb: 1,
                    }}>
                        {isEdit ? (i18n.language.startsWith('hi') ? 'फसल संपादित करें' : i18n.language.startsWith('mr') ? 'पीक संपादित करा' : 'Edit Crop') : t('cropListing.pageTitle')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 3, md: 4 } }}>
                        {[t('cropListing.breadcrumb.dashboard'), t('cropListing.breadcrumb.cropListings'), isEdit ? (i18n.language.startsWith('hi') ? 'संपादित करें' : i18n.language.startsWith('mr') ? 'संपादित करा' : 'Edit Listing') : t('cropListing.breadcrumb.createNew')].map((crumb, idx, arr) => (
                            <React.Fragment key={idx}>
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '13px',
                                    fontWeight: idx === arr.length - 1 ? 600 : 400,
                                    color: idx === arr.length - 1 ? '#1C252E' : '#637381',
                                    cursor: idx < arr.length - 1 ? 'pointer' : 'default',
                                }}>
                                    {crumb}
                                </Typography>
                                {idx < arr.length - 1 && (
                                    <Box sx={{
                                        width: '5px', height: '5px', borderRadius: '50%',
                                        backgroundColor: '#637381'
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                    </Box>

                    {/* ── Stepper ── */}
                    <Stepper steps={steps} />
                </Box>

                {/* ── Main Card ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pb: { xs: 4, md: 6 },
                }}>
                    <Box sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: { xs: '16px', md: '20px' },
                        border: '1px solid #E0E5EB',
                        boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
                        px: { xs: 3, md: 10 },
                        py: { xs: 4, md: 6 },
                    }}>
                        {/* Card heading */}
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 800,
                            fontSize: { xs: '20px', md: '26px' },
                            color: '#1C252E',
                            textAlign: 'center',
                            mb: 1,
                        }}>
                            {isEdit ? (i18n.language.startsWith('hi') ? 'अपनी लिस्टिंग संपादित करें' : i18n.language.startsWith('mr') ? 'तुमची लिस्टिंग संपादित करा' : 'Edit your listing') : t('cropListing.cardTitle')}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: { xs: '14px', md: '15px' },
                            color: '#637381',
                            textAlign: 'center',
                            mb: { xs: 4, md: 6 },
                            maxWidth: '600px',
                            mx: 'auto',
                            lineHeight: 1.6,
                        }}>
                            {t('cropListing.cardSubtitle')}
                        </Typography>

                        {/* ── Location Grid ── */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={allStates}
                                    getOptionLabel={(option) => t('states.' + (option.name || ''), { defaultValue: option.name })}
                                    value={allStates.find(s => s.name === state) || null}
                                    onChange={(event, newValue) => {
                                        setState(newValue ? newValue.name : '');
                                        setErrors(prev => ({ ...prev, state: false, district: false }));
                                    }}
                                    loading={loadingStates}
                                    popupIcon={<IconChevronDown size={20} />}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={stateRef}
                                            label={t('cropListing.state') + " *"}
                                            placeholder={t('cropListing.selectState')}
                                            error={!!errors.state}
                                            helperText={errors.state ? t('fieldReq') : ""}
                                            sx={selectSx}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={allDistricts}
                                    getOptionLabel={(option) => t('districts.' + (option || ''), { defaultValue: option })}
                                    value={district || null}
                                    onChange={(event, newValue) => {
                                        setDistrict(newValue || '');
                                        setErrors(prev => ({ ...prev, district: false }));
                                    }}
                                    disabled={!state || loadingDistricts}
                                    loading={loadingDistricts}
                                    popupIcon={<IconChevronDown size={20} />}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={districtRef}
                                            label={t('cropListing.district') + " *"}
                                            placeholder={t('cropListing.selectDistrict')}
                                            error={!!errors.district}
                                            helperText={errors.district ? t('fieldReq') : ""}
                                            sx={selectSx}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingDistricts ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {/* ── Delivery Options ── */}
                        <Box ref={optionsRef} sx={{ mb: 4 }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '14px',
                                color: errors.options ? '#FF4842' : '#1C252E',
                                mb: 1,
                            }}>
                                {t('cropListing.deliveryOptions')} *
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={options.selfPickup} onChange={handleOptionChange('selfPickup')} sx={{ color: errors.options ? '#FF4842' : '#919EAB', '&.Mui-checked': { color: PRIMARY } }} />}
                                    label={<Typography sx={{ fontFamily: fonts.publicSans || fonts.poppins, fontSize: '15px', fontWeight: 500, color: errors.options && !options.selfPickup ? '#FF4842' : 'inherit' }}>{t('cropListing.selfPickup')}</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={options.localDelivery} onChange={handleOptionChange('localDelivery')} sx={{ color: errors.options ? '#FF4842' : '#919EAB', '&.Mui-checked': { color: PRIMARY } }} />}
                                    label={<Typography sx={{ fontFamily: fonts.publicSans || fonts.poppins, fontSize: '15px', fontWeight: 500, color: errors.options && !options.localDelivery ? '#FF4842' : 'inherit' }}>{t('cropListing.localDelivery')}</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={options.transportAvailable} onChange={handleOptionChange('transportAvailable')} sx={{ color: errors.options ? '#FF4842' : '#919EAB', '&.Mui-checked': { color: PRIMARY } }} />}
                                    label={<Typography sx={{ fontFamily: fonts.publicSans || fonts.poppins, fontSize: '15px', fontWeight: 500, color: errors.options && !options.transportAvailable ? '#FF4842' : 'inherit' }}>{t('cropListing.transportAvailable')}</Typography>}
                                />
                            </Box>
                            {errors.options && (
                                <Typography sx={{ fontFamily: fonts.publicSans || fonts.poppins, fontSize: '12px', color: '#FF4842', mt: 0.5, fontWeight: 500, ml: 1 }}>
                                    {t('deliveryReq')}
                                </Typography>
                            )}
                        </Box>

                        {/* ── Additional Notes ── */}
                        <Box sx={{ mb: 5 }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '14px',
                                color: errors.notes ? '#FF4842' : '#1C252E',
                                mb: 1.5,
                            }}>
                                {t('cropListing.additionalNotes')} *
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                inputRef={notesRef}
                                placeholder={t('cropListing.notesPlaceholder')}
                                value={notes}
                                error={!!errors.notes}
                                helperText={errors.notes ? t('fieldReq') : ""}
                                onChange={(e) => {
                                    setNotes(e.target.value);
                                    if (errors.notes) setErrors(prev => ({ ...prev, notes: false }));
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '15px',
                                        backgroundColor: '#FFFFFF',
                                        '& fieldset': { borderColor: '#E0E5EB' },
                                        '&:hover fieldset': { borderColor: '#919EAB' },
                                        '&.Mui-focused fieldset': { borderColor: PRIMARY },
                                        '&.Mui-error fieldset': { borderColor: '#FF4842' },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        ml: 1,
                                    }
                                }}
                            />
                        </Box>

                        {/* ── Back + Next Buttons ── */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 3 },
                            justifyContent: 'center',
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                        }}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    color: '#1C252E',
                                    borderColor: '#E0E5EB',
                                    minWidth: { sm: '180px' },
                                    '&:hover': {
                                        borderColor: '#919EAB',
                                        backgroundColor: '#F4F6F8',
                                    },
                                }}
                            >
                                {t('cropListing.back')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePublish}
                                disabled={isPublishing}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    backgroundColor: PRIMARY,
                                    color: '#FFFFFF',
                                    minWidth: { sm: '200px' },
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: PRIMARY_DARK,
                                        boxShadow: 'none',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#A8D5B5',
                                        color: '#FFFFFF',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {isPublishing && <CircularProgress size={20} sx={{ color: 'inherit' }} />}
                                    <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                                        {isPublishing ? (isEdit ? 'Updating...' : 'Publishing...') : (isEdit ? 'Update Listing' : t('cropListing.publish'))}
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                    </Box>
                </Box>
            {/* Success Modal */}
            <Congratulations 
                open={showCongrats} 
                onClose={() => setShowCongrats(false)} 
            />
        </Box>
    );
};

export default LocationDelivery;
